import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { dsClass } from '../../../common/constants/dsClasses.js';
import {
  corporatePhoneMsg,
  costCenterAndReferenceMsg,
  costCenterMsg,
  mobilePhoneMsg,
  noUserOrPurposeOfUseSetMsg,
  numberDirectoryStatusMsg,
  purposeOfUseMsg,
  referenceIdMsg,
  t,
  userMsg,
} from '../../../common/i18n/index.js';
import { getContactPersonByContactId, getUserInfo } from '../../../common/utils/contactUtils.js';
import { getDirectoryListingViewOptions } from '../../../common/utils/subscriptionUtils.js';
import { GridColumn } from '../../BillingAccountDetails/components/GridColumn.js';
import { BbEmployeeInvitationDialog } from '../BroadBandInvitation/BbEmployeeInvitationDialog.js';
import { BbEmployeeNotFoundDialog } from '../BroadBandInvitation/BbEmployeeNotFoundDialog.js';
import { showBroadbandInvitationInfo } from '../BroadBandInvitation/bbInvitationUtils.js';
import { HighlightField, HighlightedValue } from './HighlightedValue.js';
import type { HighlightedField } from './HighlightedValue.js';
import type { PurposeOfUseOrContact } from '../../../common/types/subscription.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { DirectoryDetails } from '../../../generated/api/directoryDetails.js';
import type { Subscription } from '../../../generated/api/subscription.js';
import type { SubscriptionPbxDetails } from '../../../generated/api/subscriptionPbxDetails.js';

interface UserInfoViewProps {
  subscription: Subscription;
  contacts: Contact[];
  purposeOfUseOrContact: PurposeOfUseOrContact;
  numberPrivacyMobile?: DirectoryDetails.NumberDirectoryEnum;
  numberPrivacyCorporate?: DirectoryDetails.NumberDirectoryEnum;
  pbxType?: SubscriptionPbxDetails.PbxTypeEnum;
  changedFields?: readonly HighlightedField[];
}

export const UserInfoView = ({
  subscription,
  contacts,
  purposeOfUseOrContact,
  numberPrivacyMobile,
  numberPrivacyCorporate,
  pbxType,
  changedFields = [],
}: UserInfoViewProps) => {
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const [showNotFoundDialog, setShowNotFoundDialog] = useState(false);

  const selectedContact = contacts?.find(contact => contact.contactId === purposeOfUseOrContact.contactId);
  const userInfo = getUserInfo(selectedContact);

  const purposeOfUse = purposeOfUseOrContact.purposeOfUse;

  const purposeOfUseLabel = purposeOfUseOrContact.purposeOfUse
    ? t.Y8KX(purposeOfUseMsg)
    : t.NE0O(noUserOrPurposeOfUseSetMsg);

  const shouldShowBbInvitation = showBroadbandInvitationInfo(subscription);
  const contactPerson = getContactPersonByContactId(contacts, subscription.subscriptionContactId);

  const handleInvitationClick = () => {
    if (contactPerson) {
      setShowInvitationDialog(true);
    } else {
      setShowNotFoundDialog(true);
    }
  };

  return (
    <>
      {showInvitationDialog && (
        <BbEmployeeInvitationDialog
          contactPerson={contactPerson}
          onCloseDialog={() => setShowInvitationDialog(false)}
          subscriptionId={subscription.subscriptionId}
        />
      )}
      {showNotFoundDialog && <BbEmployeeNotFoundDialog onCloseDialog={() => setShowNotFoundDialog(false)} />}
      <CL.Grid className={dsClass.MARGIN_TOP_4}>
        {selectedContact ? (
          <CL.GridRow className="of-purpose-of-use__grid_row of-purpose-of-use__cost_center_employee_number">
            <GridColumn className="of-purpose-of-use__grid_col">
              <div>
                <span className={`${dsClass.FONT_STYLE_NORMAL} ${dsClass.FONT_WEIGHT_BOLD}`}>{t.LUZM(userMsg)}</span>
              </div>
              <HighlightedValue highlighted={changedFields.includes(HighlightField.CONTACT)}>
                {`${userInfo?.firstName?.concat(' ') || ''}${userInfo?.lastName || ''}`}
              </HighlightedValue>
            </GridColumn>
          </CL.GridRow>
        ) : (
          <CL.GridRow className="of-purpose-of-use__grid_row of-purpose-of-use__cost_center_employee_number">
            <GridColumn className="of-purpose-of-use__grid_col">
              <div>
                <span className={`${dsClass.FONT_STYLE_NORMAL} ${dsClass.FONT_WEIGHT_BOLD}`}>{purposeOfUseLabel}</span>
              </div>
              <HighlightedValue highlighted={changedFields.includes(HighlightField.PURPOSE_OF_USE)}>
                {purposeOfUse}
              </HighlightedValue>
            </GridColumn>
          </CL.GridRow>
        )}
        <h4 className={`${dsClass.MARGIN_BOTTOM_1} ${dsClass.MARGIN_TOP_6}`}>{t.JBEM(costCenterAndReferenceMsg)}</h4>
        <CL.GridRow
          className={`of-purpose-of-use__grid_row of-purpose-of-use__cost_center_employee_number ${dsClass.PADDING_TOP_2}`}
        >
          <GridColumn className="of-purpose-of-use__grid_col">
            <div>
              <span className={`${dsClass.FONT_STYLE_NORMAL} ${dsClass.FONT_WEIGHT_BOLD}`}>
                {t.L2OG(costCenterMsg)}
              </span>
            </div>
            <HighlightedValue highlighted={changedFields.includes(HighlightField.COST_CENTER)}>
              {purposeOfUseOrContact.costCenter || '-'}
            </HighlightedValue>
          </GridColumn>
          <GridColumn className="of-purpose-of-use__grid_col">
            <div>
              <span className={`${dsClass.FONT_STYLE_NORMAL} ${dsClass.FONT_WEIGHT_BOLD}`}>
                {t.CWR5(referenceIdMsg)}
              </span>
            </div>
            <HighlightedValue highlighted={changedFields.includes(HighlightField.REFERENCE_ID)}>
              {purposeOfUseOrContact.employeeNumber || '-'}
            </HighlightedValue>
          </GridColumn>
        </CL.GridRow>
        <CL.GridRow className="of-purpose-of-use__grid_row of-purpose-of-use__cost_center_employee_number">
          {numberPrivacyMobile && (
            <GridColumn className={`of-purpose-of-use__grid_col ${dsClass.PADDING_TOP_8}`}>
              <div>
                <span className={`${dsClass.FONT_STYLE_NORMAL} ${dsClass.FONT_WEIGHT_BOLD}`}>
                  {t.L8XT(numberDirectoryStatusMsg)} ({t.OWF1(mobilePhoneMsg)})
                </span>
              </div>
              <HighlightedValue highlighted={changedFields.includes(HighlightField.NUMBER_PRIVACY_MOBILE)}>
                {getDirectoryListingViewOptions().find(option => option.value === numberPrivacyMobile)?.displayValue}
              </HighlightedValue>
            </GridColumn>
          )}
          {numberPrivacyCorporate && pbxType && (
            <GridColumn className={`of-purpose-of-use__grid_col ${dsClass.PADDING_TOP_8}`}>
              <div>
                <span className={`${dsClass.FONT_STYLE_NORMAL} ${dsClass.FONT_WEIGHT_BOLD}`}>
                  {t.L8XT(numberDirectoryStatusMsg)} ({t.D9TG(corporatePhoneMsg)})
                </span>
              </div>
              <HighlightedValue highlighted={changedFields.includes(HighlightField.NUMBER_PRIVACY_CORPORATE)}>
                {getDirectoryListingViewOptions().find(option => option.value === numberPrivacyCorporate)?.displayValue}
              </HighlightedValue>
            </GridColumn>
          )}
        </CL.GridRow>
      </CL.Grid>
      {shouldShowBbInvitation && (
        <>
          <div className="of-subscription-details__company-info-header">
            {t.E45A(`Subscription information in Employee's OmaElisa`)}
          </div>
          <div>
            {t.FR97(
              `In order to get subscription information visible in Employee's OmaElisa check that the person is subscription's user and that the user information includes employee's email and phone number.`
            )}
          </div>
          <div className="of-subscription-details__employee-invitation-link">
            <CL.Button
              color="link"
              onClick={handleInvitationClick}
            >{`${t.UT0X('Send invitation to employee')}  >`}</CL.Button>
          </div>
        </>
      )}
    </>
  );
};
